import type { AsyncDataReloaderT } from '@dr.pogodin/react-global-state';
import { time } from '@dr.pogodin/react-utils';

import { listAritsts } from 'services/artists';

import {
  type ArtistListT,
  type ArtistT,
  DEFAULT_ARTIST_PAGE_SIZE,
  getId,
} from 'utils';

import { useAsyncData, useGlobalStateObject } from 'utils/global-state';

type UsePublicArtistListOptionsT = {
  pageSize?: number;
  textSearch?: string;
};

type UsePublicArtistListResT = {
  items: ArtistT[];
  loadMore: (() => void) | undefined;
  loading: boolean;
  reload: AsyncDataReloaderT<ArtistListT>;
};

/**
 * Fetches the public list of Active artists for the website (no auth required).
 * Uses a separate global state path from the admin artists.list to avoid
 * cache conflicts.
 */
export function usePublicArtistList({
  pageSize = DEFAULT_ARTIST_PAGE_SIZE,
  textSearch,
}: UsePublicArtistListOptionsT = {}): UsePublicArtistListResT {
  const gs = useGlobalStateObject();

  const updateArtistMap = (artists: ArtistT[]) => {
    const now = Date.now();
    for (const artist of artists) {
      const path = `artists.map.${getId(artist)}` as const;
      gs.set(path, { ...gs.get(path), data: artist, timestamp: now });
    }
  };

  const e = useAsyncData(
    'artists.publicList',
    async (old): Promise<ArtistListT> => {
      if (
        old?.pageSize === pageSize
        && old.textSearch === textSearch
        && old.published === true
        && !old.singlePage
      ) return old;

      const res = await listAritsts({
        asEndUser: true,
        legacyTextSearch: textSearch,
        page: 0,
        pageSize,
        published: true,
      });

      updateArtistMap(res.artists);

      return {
        hasMore: res.artists.length === pageSize,
        items: res.artists,
        lastPage: 0,
        numPages: undefined,
        pageSize,
        published: true,
        singlePage: false,
        status: 'Active',
        textSearch,
      };
    },
    {
      deps: [pageSize, textSearch],
      maxage: time.YEAR_MS,
    },
  );

  return {
    items: e.data?.items ?? [],
    loadMore: e.data?.hasMore && !e.loading ? () => {
      void e.reload(async (old): Promise<ArtistListT> => {
        if (!old) throw Error('Internal error');

        const res = await listAritsts({
          asEndUser: true,
          legacyTextSearch: textSearch,
          page: old.lastPage + 1,
          pageSize,
          published: true,
        });

        updateArtistMap(res.artists);

        const items = [...old.items];
        const itemMap: Record<string, number> = {};
        for (let i = 0; i < items.length; ++i) {
          itemMap[getId(items[i])] = i;
        }
        for (const artist of res.artists) {
          const id = getId(artist);
          const idx = itemMap[id];
          if (idx === undefined) items.push(artist);
          else items[idx] = artist;
        }

        return {
          hasMore: res.artists.length === pageSize,
          items,
          lastPage: old.lastPage + 1,
          numPages: undefined,
          pageSize,
          published: true,
          singlePage: false,
          status: 'Active',
          textSearch,
        };
      });
    } : undefined,
    loading: e.loading,
    reload: e.reload,
  };
}
